/**
 * safeReturnTo — validate a `returnTo` query value before redirecting to it.
 *
 * The auth flow round-trips a destination through `?returnTo=` so a deep link
 * survives sign-in (this is what keeps the Revit bridge's
 * `?revitBridge=true&wsPort=NNNN` params alive across the auth bounce — see
 * bug f0e3fa26). An attacker-supplied value must never redirect off-origin:
 * a same-origin 3xx from vaon.app is a phishing laundering primitive.
 *
 * Validate by CONSTRUCTION, never by string prefix. WHATWG URL parsing folds
 * "\" into "/" for special schemes and strips ASCII tab/newline before
 * parsing, so `startsWith("/") && !startsWith("//")` still admits values like
 * "/\evil.com" and "/\t/evil.com" that resolve to https://evil.com/.
 * Comparing the RESOLVED origin closes that whole class, including parser
 * quirks not yet known.
 *
 * Returns a path+query+hash string safe to hand to `router.push` /
 * `NextResponse.redirect`, or `fallback` when the value is missing, absolute
 * to another origin, or unparseable.
 */
export function safeReturnTo(
  returnTo: string | null | undefined,
  currentUrl: string,
  fallback = "/projects"
): string {
  if (!returnTo) return fallback;
  try {
    const base = new URL(currentUrl);
    const dest = new URL(returnTo, base);
    if (dest.origin !== base.origin) return fallback;
    const path = `${dest.pathname}${dest.search}${dest.hash}`;
    // A resolved same-origin URL always yields an absolute path; guard anyway
    // so a pathological parse can't produce a protocol-relative string.
    return path.startsWith("//") ? fallback : path;
  } catch {
    return fallback;
  }
}
